import { Button, Stack, Typography } from '@mui/material'
import { forwardRef, useEffect, useState } from 'react'
import { UseFormSetValue } from 'react-hook-form'
import { Icons, Select, TextInputField, TextInputFieldProps } from 'utoniq-ui'

import {
  Maybe,
  MembershipSubscriptionPlan,
  Post,
  PostCategoryDetail,
  PostStatus,
  PostType,
  UpdatePostInput,
} from '@/clients/utoniq-core/schema'
import { ConsolePostReserve } from '@/components/pages/ConsolePostCreate/template/ConsolePostReserve'
import {
  handleError,
  tLabel,
  useMessage,
  useRouter,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Organism } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { captureError } from '@/core/errors'
import { Form, useForm, UseFormProps } from '@/core/form'

import { PostEditConfirmModal } from './PostEditConfirmModal'

export type PostEditFormProps = {
  onSubmitPostEdit: (values: UpdatePostInput) => Promise<void>
  artistId: string
  post: Pick<
    Post,
    | 'id'
    | 'content'
    | 'isPremium'
    | 'publishedAt'
    | 'planId'
    | 'planTitle'
    | 'resource'
    | 'resourceType'
    | 'status'
  > & {
    category?: Maybe<Pick<PostCategoryDetail, 'id' | 'name'>>
  }
  plans: Maybe<Pick<MembershipSubscriptionPlan, 'planId' | 'title'>>[]
  loadingEditPost: boolean
  categories: Maybe<Pick<PostCategoryDetail, 'id' | 'name'>>[]
}

export type PostEditValues = {
  content: string
  membershipAccessPermission: string
  categoryId: string
  publishedAt?: string
}

const schema: UseFormProps['schema'] = o => ({
  content: o.string().max(10000).label(tLabel('consolePostEdit.content')),
  categoryId: o.string().optional().label(tLabel('consolePostEdit.categoryId')),
  membershipAccessPermission: o
    .string()
    .label(tLabel('consolePostCreate.membershipAccessPermission')),
  publishedAt: o
    .date()
    .allow(null)
    .optional()
    .label(tLabel('consolePostEdit.publishedAt')),
})

// カテゴリリストの初期値
const defaultCategory = {
  none: 'noneCategory',
}

// ポスト公開範囲リストの初期値
const defaultPermission = {
  allUser: 'allUser',
  allMember: 'allMember',
}

const defaultValues: PostEditValues = {
  content: '',
  membershipAccessPermission: defaultPermission.allUser,
  categoryId: defaultCategory.none,
}
export const PostEditForm: Organism<PostEditFormProps> = ({
  post,
  onSubmitPostEdit,
  artistId,
  plans,
  loadingEditPost,
  categories,
}) => {
  const { t } = useTranslation('consolePostEdit')
  const { showMessage, showError } = useMessage()
  const { router, routerPages } = useRouter()
  const {
    handleSubmit,
    formState: { errors, isValid },
    register,
    reset,
    watch,
    setValue,
    setError,
    getValues,
  } = useForm<PostEditValues>({
    schema,
    defaultValues: {
      ...defaultValues,
      categoryId: post.category?.id ?? defaultCategory.none,
    },
  })

  const { ref, ...rest } = register('membershipAccessPermission')

  useEffect(() => {
    reset({
      content: post?.content ?? '',
      membershipAccessPermission: post.isPremium
        ? post.planId
          ? plans.find(plan => plan?.planId === post.planId)?.planId
          : defaultPermission.allMember
        : defaultPermission.allUser,
      publishedAt: post?.publishedAt,
      categoryId: post.category?.id ? post.category?.id : defaultCategory.none,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [post.publishedAt, post.content, post.planId, post.category?.id])

  const [confirmModal, setConfirmModal] = useState<
    { confirmAction: (confirmed: boolean) => void } | undefined
  >()
  const onEditPost = async () => {
    const confirmed = await new Promise<boolean>(resolve => {
      setConfirmModal({
        confirmAction: resolve,
      })
    })
    // キャンセルされた場合、モーダルを閉じて終了
    if (!confirmed) {
      setConfirmModal(undefined)
      return
    }
    const { membershipAccessPermission, ...inputs } = getValues()

    return onSubmitPostEdit({
      ...inputs,
      /**
       * 公開範囲のキーからisPremiumとplanIdの値をミューテーション引数に割り当てる
       * プランを指定しない場合はplanId: nullになる
       */
      isPremium: membershipAccessPermission !== defaultPermission.allUser,
      planId: Object.values(defaultPermission).includes(
        membershipAccessPermission
      )
        ? null
        : membershipAccessPermission,
      // カテゴリIDがnoneCategoryの場合はカテゴリ未指定としてnullを渡す
      categoryId:
        inputs.categoryId === defaultCategory.none ? null : inputs.categoryId,
    })
      .then(() => {
        showMessage(t('completeEdit'))
        inputs.publishedAt
          ? router.replace(routerPages.ConsoleUnpublishedPosts)
          : router.push(routerPages.ConsolePost)
      })
      .catch(e => {
        captureError(e, 'ConsolePostEdit')
        handleError<PostEditValues>(e, setError, showError)
      })
      .finally(() => {
        setConfirmModal(undefined)
      })
  }

  return (
    <>
      <Form onSubmit={handleSubmit(onEditPost)} sx={{ p: 5 }}>
        {(post.resourceType === PostType.Video ||
          post.resourceType === PostType.Image) && (
          <CellFrame sx={{ mb: 6 }}>
            <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
              <Stack flex={1}>
                <Typography variant="h5">{t('postCategory')}</Typography>
              </Stack>
              <Stack>
                <Button
                  variant="text"
                  onClick={() => router.push(routerPages.ConsolePostCategories)}
                  sx={{ textAlign: 'right', alignItems: 'flex-end' }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    {t('editCategory')}
                  </Stack>
                </Button>
              </Stack>
            </Stack>
            <Select
              listItems={[
                ...Object.values(defaultCategory).map(value => ({
                  value: value,
                  label: t(value),
                })),
                ...(categories
                  ? categories.map(category => ({
                      value: category?.id ?? '',
                      label: category?.name ?? '',
                    }))
                  : []),
              ]}
              value={watch('categoryId')}
              onChange={value => {
                setValue('categoryId', value)
              }}
              formControlProps={{
                sx: {
                  width: '100%',
                  bgcolor: 'grey.50',
                  minHeight: 'auto',
                  mb: 16,
                },
              }}
            />
          </CellFrame>
        )}
        <TextInputCell
          type="textarea"
          textFieldProps={{
            variant: 'standard',
            multiline: true,
            minRows: 5,
            maxRows: 12,
            InputProps: {
              sx: {
                px: 0,
                pt: 2,
                bgcolor: 'background.default',
              },
              disableUnderline: true,
            },
          }}
          titleProps={{ variant: 'h5', color: 'text.primary' }}
          error={errors?.content}
          {...register('content')}
        />
        {!!post?.publishedAt && post?.status != PostStatus.Published && (
          <ReserveInputCell
            artistId={artistId}
            setValue={setValue}
            value={watch('publishedAt')}
          />
        )}
        {/* 公開範囲を選択する */}
        <CellFrame sx={{ mb: 6 }}>
          <Typography variant="h5" sx={{ mb: 2 }}>
            {t('membershipAccessPermission')}
          </Typography>
          <Select
            listItems={[
              ...Object.values(defaultPermission).map(value => ({
                value: value,
                label: t(value),
              })),
              ...(plans.length
                ? plans.map(plan => ({
                    value: plan?.planId ?? '',
                    label: plan?.title ?? '',
                  }))
                : []),
            ]}
            value={watch('membershipAccessPermission').toString()}
            onChange={value => {
              setValue('membershipAccessPermission', value)
            }}
            formControlProps={{
              sx: {
                width: '100%',
                bgcolor: 'grey.50',
                minHeight: 'auto',
                mb: 16,
              },
            }}
          />
        </CellFrame>
        <ButtonCell
          loading={loadingEditPost}
          isReserve={!!watch('publishedAt')}
        />
      </Form>
      <PostEditConfirmModal
        open={!!confirmModal}
        onClose={() => confirmModal?.confirmAction(false)}
        onClick={() => confirmModal?.confirmAction(true)}
        loading={loadingEditPost}
      />
    </>
  )
}

const TextInputCell = forwardRef(
  (props: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consolePostEdit')
    return (
      <CellFrame>
        <TextInputField {...props} ref={ref} t={t} />
      </CellFrame>
    )
  }
)

type ReserveInputCellProps = {
  artistId: string
  value: PostEditValues['publishedAt']
  setValue: UseFormSetValue<PostEditValues>
}
const ReserveInputCell: Cell<ReserveInputCellProps> = ({
  artistId,
  setValue,
  value,
}) => {
  const { t } = useTranslation('consolePostCreate')
  const [open, setOpen] = useState<boolean>(false)

  return (
    <CellFrame sx={{ py: 3 }}>
      <Button
        variant="text"
        startIcon={<Icons.Calendar sx={{ color: 'primary.main' }} />}
        onClick={() => setOpen(true)}
        sx={{ textAlign: 'left', alignItems: 'flex-start' }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          {value
            ? t('postPostedOn', {
                date: DateTime.formatDate(value, 'LL HH:mm') || '',
                fallback: 'このポストは{{-date}}に投稿されます。',
              })
            : t('postDateTime', '投稿日時を指定する')}
        </Stack>
      </Button>
      <ConsolePostReserve
        open={open}
        onClose={() => setOpen(false)}
        artistId={artistId}
        defaultValue={value}
        onSubmit={async value => {
          setValue('publishedAt', value.publishedAt as never)
        }}
        /**
         * @description
         * Open when function is implemented in the future
         * https://app.clickup.com/t/908127/UT-9308
         */
        // onDelete={() => {
        //   setValue('publishedAt', undefined as never)
        // }}
      />
    </CellFrame>
  )
}

const ButtonCell: Cell<{ loading: boolean; isReserve: boolean }> = ({
  loading,
  isReserve,
}) => {
  const { t } = useTranslation('consolePostEdit')
  return (
    <CellFrame sx={{ px: 5, textAlign: 'center' }}>
      <Button type="submit" size="large" sx={{ width: 244 }} loading={loading}>
        {!isReserve ? t('submit') : t('reserve')}
      </Button>
    </CellFrame>
  )
}
